import { FC } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { useEndpoints } from '../../../hooks/useEndpoints';
import GenericModal from '../../../shared/GenericModal/GenericModal';
import CustomAvatar from '../../../shared/CustomAvatar/CustomAvatar';
import LeopartLoader from '../../../shared/LeopartLoader/LeopartLoader';
import { ChartErrorState } from '../AthleteInjuryCharts/charts/_shared/ChartErrorState';
import { getInitials } from '../../../utils/functions';
import { addBaseUrl } from '../../../lib/DurationPicker/utils';
import { getIntensityLabel } from '../../../shared/bodyMap/bodyMapScale';
import styles from './ZoneAthletesModal.module.scss';

interface ZoneAthlete {
  athleteId: string;
  firstName: string;
  lastName: string;
  image?: string;
  maxIntensity: number;
  complaintCount: number;
  lastDate: string;
}

interface ZoneAthletesResponse {
  teamId: string;
  zone: string;
  period: string;
  athletes: ZoneAthlete[];
}

interface Props {
  teamId: string;
  zone: string;
  zoneLabel: string;
  period: string;
  periodLabel: string;
  onClose: () => void;
}

type IntensityBand = 'mild' | 'moderate' | 'intense';

/** Fascia di intensità (per scegliere la classe CSS), coerente con `getIntensityLabel`. */
function intensityBand(maxIntensity: number): IntensityBand {
  const label = getIntensityLabel(maxIntensity);
  if (label === 'Intensa') return 'intense';
  if (label === 'Moderata') return 'moderate';
  return 'mild';
}

const INTENSITY_DOT_CLASS: Record<IntensityBand, string> = {
  mild: styles.intensityDotMild,
  moderate: styles.intensityDotModerate,
  intense: styles.intensityDotIntense,
};

/**
 * Drill-down atleti su una zona — modal coerente col design system
 * (`GenericModal`: Chakra Dialog con Esc/focus-trap/scroll-lock già gestiti).
 */
const ZoneAthletesModal: FC<Props> = ({
  teamId,
  zone,
  zoneLabel,
  period,
  periodLabel,
  onClose,
}) => {
  const endpoints = useEndpoints();

  const { data, isLoading, isError, refetch } = useQuery<ZoneAthletesResponse>({
    queryKey: ['bodymap', 'team', teamId, 'zone', zone, period],
    queryFn: async () => {
      const { data } = await endpoints.getBodyMapTeamZone(teamId, zone, {
        period,
      });
      return data as ZoneAthletesResponse;
    },
    enabled: !!teamId && !!zone,
  });

  const athletes = data?.athletes ?? [];

  return (
    <GenericModal
      isOpen
      onClose={onClose}
      size="sm"
      Header={
        <div className={styles.header}>
          <div className={styles.title}>{zoneLabel}</div>
          <div className={styles.subtitle}>
            Atleti con segnalazioni · Ultimi {periodLabel}
          </div>
        </div>
      }
    >
      {isLoading ? (
        <div className={styles.loading}>
          <LeopartLoader size="sm" />
        </div>
      ) : isError ? (
        <div className={styles.errorState}>
          <ChartErrorState onRetry={() => refetch()} />
        </div>
      ) : athletes.length === 0 ? (
        <div className={styles.empty}>Nessun atleta con segnalazioni su questa zona</div>
      ) : (
        <div className={styles.list}>
          {athletes.map((a) => {
            const fullName = [a.firstName, a.lastName].filter(Boolean).join(' ');
            const band = intensityBand(a.maxIntensity);
            return (
              <Link
                key={a.athleteId}
                to={`/dashboard/users/${a.athleteId}`}
                className={styles.row}
                onClick={onClose}
              >
                <CustomAvatar
                  name={getInitials(fullName)}
                  src={a.image ? addBaseUrl(1, a.image) : undefined}
                  className={styles.avatar}
                />
                <div className={styles.info}>
                  <div className={styles.name}>{fullName}</div>
                  <div className={styles.meta}>
                    {a.complaintCount} segnalazion{a.complaintCount === 1 ? 'e' : 'i'}
                    {' · ultima '}
                    {dayjs(a.lastDate).format('DD MMM')}
                  </div>
                </div>
                <div className={styles.intensity}>
                  <span className={[styles.intensityDot, INTENSITY_DOT_CLASS[band]].join(' ')} />
                  <span className={styles.intensityValue}>
                    {a.maxIntensity}/5
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </GenericModal>
  );
};

export default ZoneAthletesModal;
